import { signal, Injectable, inject } from "@angular/core";
import { ApiService } from "./api.service";
import { NavigationEnd, Router } from "@angular/router";
import { AuthService } from "./auth.service";
import { filter, take } from "rxjs";
import { GroupResolverService } from './group-resolver.service';

@Injectable({ providedIn: "root" })
export class AppInitializerService {
  private authService = inject(AuthService);
  private router = inject(Router);
  public loading = signal(true); // Start as loading
  private hasDoneInitialRedirect = false; // Bandera de control
  private apiService = inject(ApiService)
  private readonly PUBLIC_AUTH_PATHS = [
    "/auth/login",
    "/auth/register",
    "/auth/forgot-password",
    "/auth/confirm-register",
    "/auth/reset-password",
  ];
  constructor(private groupResolver: GroupResolverService) { }
  async initializeUserData(): Promise<void> {
    let userId = "";
    const currentPath = this.getCurrentPath();
    const isPublicAuthRoute = this.isPublicAuthPath(currentPath);

    this.loading.set(true);
    try {
      const session = sessionStorage.getItem('session');

      // Check session validity without session_id
      const isAuthenticated = await this.authService.isAuthenticated();
      if (!isAuthenticated || !session || session === 'false') {
        if (isPublicAuthRoute) {
          return;
        }

        const authUserRaw = localStorage.getItem('auth_user');
        if (authUserRaw) {
          const authUser = JSON.parse(authUserRaw);
          userId = authUser?.username;
          if (userId) {
            const userType = await this.groupResolver.getUserType();
            if (userType === "prospect")
              await this.apiService.clearProspectSession();
            else if (userType === "customer")
              await this.apiService.clearCustomerSession();
          }
        }

        await this.authService.logout();
        this.router.navigate(['/auth/login']);
        return;
      }
      // Try to get userId from Cognito LastAuthUser or your app's storage
      const lastAuthKey = Object.keys(localStorage).find((k) =>
        k.endsWith("LastAuthUser")
      );
      if (lastAuthKey) {
        userId = localStorage.getItem(lastAuthKey) || "";
      }
      if (!userId) {
        const authUserRaw = localStorage.getItem("auth_user");
        if (authUserRaw) {
          const authUser = JSON.parse(authUserRaw);
          userId = authUser?.username || authUser?.id || "";
        }
      }

      if (!userId) {
        this.router.navigate(["/auth/login"]); // Redirect to login if no userId found
        return; // If no user, don't call anything
      }

      // Determine group via resolver (Cognito preferred, backend fallback)
      let groupVal = "";
      try {
        groupVal = await this.groupResolver.getUserType();
        // Now, call the correct /me API based on group
        if (groupVal === "customer") {
          await this.apiService.customerMe();
          await this.apiService.fetchCustomerDocuments();
          await this.apiService.fetchContracts("contract");
          await this.apiService.accountBalanceList();
        } else if (groupVal === "prospect") {
          await this.apiService.prospectMe();
          await this.apiService.fetchProspectDocuments();
        }
      } catch (err) {
        console.error("Error during group/profile initialization:", err);
        let userType: string | undefined;
        if (userId) {
          try {
            userType = await this.groupResolver.getUserType();
            if (userType === "prospect")
              await this.apiService.clearProspectSession();
            else if (userType === "customer")
              await this.apiService.clearCustomerSession();
          } catch (e) {
            console.error('Error determining user type:', e);
          }
        }
        await this.authService.logout();
        this.router.navigate(['/auth/login']);
      }
    } finally {
      this.loading.set(false);
    }
  }

  private getCurrentPath(): string {
    return this.normalizePath(window.location.pathname || "");
  }

  private isPublicAuthPath(pathname: string): boolean {
    const normalizedPath = this.normalizePath(pathname);
    return this.PUBLIC_AUTH_PATHS.some((path) => this.normalizePath(path) === normalizedPath);
  }

  private normalizePath(pathname: string): string {
    const rawPath = (pathname || "").split("?")[0].split("#")[0].trim();
    if (!rawPath) {
      return "/";
    }

    const noTrailingSlash = rawPath.length > 1
      ? rawPath.replace(/\/+$/, "")
      : rawPath;

    return noTrailingSlash.toLowerCase();
  }
}
